import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    Divider,
    Tooltip,
    alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import NoteIcon from '@mui/icons-material/Note';

const ProjectMapOverlay = ({ 
    isOpen, 
    onClose, 
    sections, 
    translatedSections, 
    approvedSections, 
    tagMaps, 
    onSegmentClick, 
    currentSegmentIndex 
}) => {
    const [openPages, setOpenPages] = useState({});

    const groupedSegments = useMemo(() => {
        const groups = {};
        sections.forEach((text, index) => {
            const tm = tagMaps && tagMaps[index] ? tagMaps[index] : {};
            const page = tm._pageContext || 'General';
            if (!groups[page]) {
                groups[page] = [];
            }
            
            // Determine status
            const translatedText = translatedSections[index] || '';
            const isApproved = approvedSections.includes(index);
            let status = 'missing';
            if (isApproved) {
                status = 'translated';
            } else if (translatedText.trim().length > 0) {
                status = 'in-progress';
            }

            groups[page].push({
                index,
                text: text.length > 50 ? text.substring(0, 50) + '...' : text,
                status
            });
        });
        return groups;
    }, [sections, tagMaps, translatedSections, approvedSections]);

    const togglePage = (page) => {
        setOpenPages(prev => ({
            ...prev,
            [page]: !prev[page]
        }));
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'translated':
                return <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />;
            case 'in-progress':
                return <AutorenewIcon sx={{ fontSize: 16, color: '#f59e0b' }} />;
            default:
                return <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />;
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop for closing */}
            <Box 
                onClick={onClose}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                    zIndex: 1299,
                    backdropFilter: 'blur(2px)'
                }}
            />
            
            <Box
                sx={{
                    position: 'fixed',
                    top: 15,
                    left: 15,
                    width: 360,
                    height: 'calc(100vh - 30px)',
                    bgcolor: alpha('#ffffff', 0.9),
                    backdropFilter: 'blur(15px)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    zIndex: 1300,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    transform: isOpen ? 'translateX(0) scale(1)' : 'translateX(-120%) scale(0.9)',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    color: 'white'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ 
                            p: 1, 
                            borderRadius: '10px', 
                            bgcolor: 'rgba(255,255,255,0.1)',
                            display: 'flex'
                        }}>
                            <AccountTreeIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>
                                Project Map
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                {sections.length} segments structure
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#e2e8f0', borderRadius: '10px' } }}>
                    <List component="nav" sx={{ p: 0 }}>
                        {Object.keys(groupedSegments).map((page) => (
                            <Box key={page} sx={{ mb: 1.5, borderRadius: '12px', overflow: 'hidden', border: '1px solid #f1f5f9', bgcolor: 'white' }}>
                                <ListItemButton 
                                    onClick={() => togglePage(page)}
                                    sx={{ 
                                        py: 1.5, 
                                        px: 2,
                                        bgcolor: openPages[page] ? '#f8fafc' : 'transparent',
                                        '&:hover': { bgcolor: '#f8fafc' }
                                    }}
                                >
                                    <ListItemText 
                                        primary={`Page ${page}`} 
                                        primaryTypographyProps={{ 
                                            fontWeight: 700, 
                                            fontSize: '0.9rem',
                                            color: '#1e293b'
                                        }}
                                        secondary={`${groupedSegments[page].length} segments`}
                                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                                    />
                                    {openPages[page] ? <ExpandLess sx={{ color: '#64748b' }} /> : <ExpandMore sx={{ color: '#64748b' }} />}
                                </ListItemButton>
                                
                                <Collapse in={openPages[page]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding sx={{ bgcolor: '#ffffff' }}>
                                        {groupedSegments[page].map((segment, idx) => (
                                            <React.Fragment key={segment.index}>
                                                <Divider sx={{ borderStyle: 'dashed', borderColor: '#f1f5f9' }} />
                                                <ListItemButton 
                                                    sx={{ 
                                                        pl: 3,
                                                        py: 1.2,
                                                        transition: 'all 0.2s',
                                                        bgcolor: currentSegmentIndex === segment.index ? alpha('#1e293b', 0.05) : 'transparent',
                                                        borderLeft: currentSegmentIndex === segment.index ? '4px solid #1e293b' : '4px solid transparent',
                                                        '&:hover': { 
                                                            bgcolor: alpha('#1e293b', 0.03),
                                                            pl: 3.5
                                                        }
                                                    }}
                                                    onClick={() => onSegmentClick(segment.index)}
                                                >
                                                    <Box sx={{ mr: 2, display: 'flex' }}>
                                                        {getStatusIcon(segment.status)}
                                                    </Box>
                                                    <ListItemText 
                                                        primary={segment.text || '(Empty segment)'} 
                                                        primaryTypographyProps={{ 
                                                            variant: 'body2',
                                                            noWrap: true,
                                                            fontWeight: currentSegmentIndex === segment.index ? 600 : 400,
                                                            fontSize: '0.85rem',
                                                            color: segment.text ? '#334155' : '#94a3b8'
                                                        }}
                                                        secondary={`#${segment.index + 1}`}
                                                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                                                    />
                                                </ListItemButton>
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Collapse>
                            </Box>
                        ))}
                    </List>
                </Box>
                
                {/* Footer / Legend */}
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 2, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <RadioButtonUncheckedIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>Missing</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AutorenewIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>In Progress</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>Translated</Typography>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default ProjectMapOverlay;
